import { hasAllPermissions, type Me } from "@app/api-client";

import { aggregatePages } from "./aggregate/pages";
import { masterPages } from "./master/pages";
import type { ServiceModule, ServicePage } from "./types";

/**
 * 前端已實作的服務模組。**新增服務時只要在這裡加一筆。**
 *
 * 這不是「服務清單」—— 那來自 `/me/`，由後端決定使用者能看到什麼。
 * 這裡宣告的是「前端為哪些服務準備了頁面」。兩者的關係是：
 *
 *   /me/ 說使用者能進 hr 服務，但這裡沒有 hr 的模組
 *     → 側欄有入口（服務存在），點進去是 404（頁面還沒寫）
 *
 *   這裡有 hr 的模組，但 /me/ 沒給使用者 hr 服務
 *     → 側欄不顯示，路由的 RequirePermission 擋下來
 *
 * 兩種情況都是刻意的：前端與後端的上線步調不需要綁在一起。
 */
export const SERVICE_MODULES: ServiceModule[] = [
  { code: "master", pages: masterPages },
  { code: "aggregate", pages: aggregatePages },
];

// key 刻意是 string 而不是 ServiceCode：查詢的來源是網址，執行期一定會
// 遇到前端還沒實作的服務代碼，那不是錯誤而是正常狀態（見上面的說明）。
const BY_CODE = new Map<string, ServiceModule>(
  SERVICE_MODULES.map((module) => [module.code, module]),
);

/** 這個服務在前端宣告的所有頁面。前端沒實作的服務回空陣列。 */
export function servicePages(code: string): ServicePage[] {
  return BY_CODE.get(code)?.pages ?? [];
}

/**
 * 使用者看得到的頁面。
 *
 * 沒權限的頁面不出現在側欄 —— 但這只是 UX，路由層的 `RequirePermission`
 * 與後端各自還有一道。
 */
export function visibleServicePages(code: string, me: Me | null | undefined): ServicePage[] {
  return servicePages(code).filter(
    (page) => !page.permissions?.length || hasAllPermissions(me, page.permissions),
  );
}

/** 子頁的完整路徑。空的 `path` 就是服務根路徑。 */
export function pageHref(serviceCode: string, path: string): string {
  return path ? `/${serviceCode}/${path}` : `/${serviceCode}`;
}

/**
 * 依目前的網址找出是哪一個子頁。
 *
 * 由長到短比對，這樣 `/master/items/42` 會對到 `items` 而不是首頁。
 */
export function activeServicePage(
  serviceCode: string,
  pathname: string,
): ServicePage | undefined {
  const pages = [...servicePages(serviceCode)].sort((a, b) => b.path.length - a.path.length);
  return pages.find((page) => {
    const href = pageHref(serviceCode, page.path);
    return pathname === href || pathname.startsWith(`${href}/`);
  });
}

/** 頁面的顯示名稱（麵包屑與大標用），沒給 title 就沿用 label。 */
export function pageTitle(page: ServicePage): string {
  return page.title ?? page.label;
}
